// src/services/relationships.js
// Takip servisleri.
// Mimari: users/{targetUid}/followers/{meUid} + users/{meUid}/following/{targetUid}
// Sayaç güncellemeleri Cloud Function trigger'ları tarafından atomik yapılır.
//
// P0-2 (Profil Derin Analiz V2) — follow()/unfollow() artık doğrudan
// Firestore yazmıyor; followUserV3/unfollowUserV3 callable'larını çağırır.
// Callable admin SDK ile aynı doc'ları yazar + kullanıcı başına 60 sn'de
// toplam 30 işlem rate limit'i uygular (aşımda resource-exhausted /
// "RATE_LIMITED" → mapError kullanıcı dostu mesaja çevirir).
//
// ÖNEMLİ: Eski /follows kök koleksiyonuna YAZILMAZ — Cloud Function çift sayar.
// watchIsFollowing() sadece users/{target}/followers/{me} izler — yarış koşulu yok.

import { auth, db } from "../firebase";
import { durumCoz } from "./iliskiDurumu";
import type { IliskiDurumu } from "./iliskiDurumu";
import {
  doc,
  collection,
  getDocs,
  getDoc,
  onSnapshot,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { limitMesajiTR } from "../utils/kotaMesaji"; // EMİR #95.1

const REGION = "europe-west3";

function requireUser() {
  const u = auth.currentUser;
  if (!u) throw new Error("AUTH_REQUIRED");
  return u;
}

/** users/{targetUid}/followers/{followerUid} */
function followerRef(targetUid: string, followerUid: string) {
  return doc(db, "users", String(targetUid), "followers", String(followerUid));
}

/**
 * `follow()` dönüşü — FAZ 0 sözleşmesi (aşağıdaki JSDoc bloğunda anlatılıyor).
 */
export type TakipDurumu = "following" | "requested";

/**
 * Takip et (P0-2: rate-limited callable üzerinden).
 * followUserV3 admin SDK ile users/{target}/followers/{me} +
 * users/{me}/following/{target} yazar → mevcut counter trigger'ları
 * (onFollowersCreate, sharded counter) aynen tetiklenir.
 * Limit aşımında callable resource-exhausted ("RATE_LIMITED") fırlatır.
 *
 * FAZ 0 — Dönüş artık durum bildirir:
 *   "following": takip edildi (hedef açık hesap veya zaten takipteydik)
 *   "requested": hedef GİZLİ hesap — server followRequest oluşturdu,
 *                follower edge'i YAZILMADI. UI "İstek Gönderildi" göstermeli.
 * Engel (iki yönden biri) varsa permission-denied / "BLOCKED" fırlatır.
 */
export async function follow(
  targetUid: string,
  sourceClipId: string | null = null,
): Promise<TakipDurumu> {
  const me = requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");
  if (me.uid === targetUid) throw new Error("SELF_FOLLOW_FORBIDDEN");

  const callable = httpsCallable(getFunctions(undefined, REGION), "followUserV3");
  // BAĞLA — takip bir klipten geldiyse sourceClipId iletilir; server o klibin
  // ranking sayacını (v3_followFromClipCount) artırır. Opsiyonel, geriye uyumlu.
  const payload: any = { targetUid: String(targetUid) };
  if (sourceClipId) payload.sourceClipId = String(sourceClipId);
  const res = await callable(payload);
  const status = (res?.data as any)?.status === "requested" ? "requested" : "following";
  return status;
}

/**
 * Takibi bırak (P0-2: rate-limited callable üzerinden, follow ile ortak pencere).
 */
export async function unfollow(targetUid: string): Promise<true> {
  requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");

  const callable = httpsCallable(getFunctions(undefined, REGION), "unfollowUserV3");
  await callable({ targetUid: String(targetUid) });
  return true;
}

/**
 * FAZ 1A — Takipçi çıkar (remove follower, Instagram paritesi).
 * Profil sahibi kendi takipçi listesinden targetUid'i sessizce düşürür.
 * Rules owner'a client-side delete izni vermez → removeFollowerV3
 * callable (admin SDK) üzerinden.
 */
export async function removeFollower(targetUid: string): Promise<true> {
  requireUser();
  if (!targetUid) throw new Error("TARGET_REQUIRED");

  const callable = httpsCallable(getFunctions(undefined, REGION), "removeFollowerV3");
  await callable({ targetUid: String(targetUid) });
  return true;
}

/**
 * FAZ 1B — "Seni takip ediyor" rozeti için one-shot kontrol:
 * targetUid beni takip ediyor mu? (users/{me}/followers/{target})
 */
export async function getFollowsMeOnce(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) return false;
  try {
    const snap = await getDoc(followerRef(me.uid, targetUid));
    return snap.exists();
  } catch {
    return false;
  }
}

export async function getIsFollowingOnce(targetUid: string): Promise<boolean> {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) return false;
  const snap = await getDoc(followerRef(targetUid, me.uid));
  return snap.exists();
}

/**
 * Takip durumunu canlı izle.
 * SADECE users/{target}/followers/{me} izler — yarış koşulu yok.
 * Eski /follows kök koleksiyonu izlenmez.
 */
export function watchIsFollowing(
  targetUid: string,
  cb: (takipEdiyor: boolean, durum: IliskiDurumu) => void,
): () => void {
  const me = auth.currentUser;
  if (!me || !targetUid || me.uid === targetUid) {
    // Muhafız dalı GERÇEK cevap (kendinle ilişki kurulamaz) → `hayir`.
    cb(false, "hayir");
    return () => {};
  }

  // EMİR #97 / A5 — hata dalı artık "hayır" DEMİYOR. İkinci argüman üç
  // değerli durum (`iliskiDurumu.ts`); yalnız boolean okuyan eski çağıranlar
  // ETKİLENMEZ (watchIsMuted'ın muteType deseniyle aynı).
  const unsub = onSnapshot(
    followerRef(targetUid, me.uid),
    (snap) => cb(snap.exists(), durumCoz(snap.exists())),
    () => cb(false, durumCoz(false, true)),
  );

  return unsub;
}

/** `watchCounts` geri-çağrısının taşıdığı sayaçlar (ikisi de normalize). */
export type TakipSayaclari = { followersCount: number; followingCount: number };

/** Sayaçları users/{uid} dokümanından izle */
export function watchCounts(
  userUid: string | null | undefined,
  cb: (sayaclar: TakipSayaclari) => void,
): () => void {
  if (!userUid) return () => {};
  const userDoc = doc(db, "users", String(userUid));
  return onSnapshot(
    userDoc,
    (snap) => {
      const d = snap.data() || {};
      cb({
        followersCount: Number(d.followersCount || 0),
        followingCount: Number(d.followingCount || 0),
      });
    },
    () => cb({ followersCount: 0, followingCount: 0 })
  );
}

/**
 * Faz 2 (Ölçeklenebilirlik) — Sharded takipçi sayacı okuma (ADDITIVE).
 *
 * users/{uid}/counters/followers_shard_* doc'larının `count` toplamını döner.
 * Viral profilde denorm followersCount tek-doc hot-write yerine bu dağıtık
 * shard'lardan okunur (Cloud Function follower_counter_sharded.ts yazar).
 *
 * Dönüş sözleşmesi:
 *   - number  → shard'lar bulundu, toplam (0 olabilir = gerçekten 0 takipçi).
 *   - null    → shard yok (backfill yapılmamış) veya okuma hatası → caller
 *               denorm followersCount'a fallback etmeli.
 *
 * NOT: Yalnız backfill marker'ı (users/{uid}.followersShardedAt) olan
 * profiller için çağrılmalı; aksi halde shard'lar kısmi (yalnız deploy
 * sonrası delta'lar) olabilir ve yanlış toplam verir.
 */
export async function readFollowerShardSum(
  uid: string | null | undefined,
): Promise<number | null> {
  if (!uid) return null;
  try {
    const snap = await getDocs(collection(db, "users", String(uid), "counters"));
    let sum = 0;
    let found = false;
    snap.forEach((d) => {
      if (String(d.id || "").startsWith("followers_shard_")) {
        found = true;
        const v = Number((d.data() || {}).count || 0);
        if (Number.isFinite(v)) sum += v;
      }
    });
    return found ? Math.max(0, sum) : null;
  } catch {
    return null;
  }
}

export function mapError(e: unknown): string {
  // `throw new Error(...)` (message) veya FirebaseError (code + details).
  const hata = e as
    | { message?: string; code?: string; details?: { retryAfterMs?: unknown } }
    | null
    | undefined;
  const code = String(hata?.message || hata?.code || e || "ERR");
  if (code.includes("AUTH_REQUIRED")) return "Giriş yapmalısın.";
  if (code.includes("SELF_FOLLOW_FORBIDDEN")) return "Kendini takip edemezsin.";
  // FAZ 5 P2-3 — günlük limit RATE_LIMITED'den ÖNCE kontrol edilmeli:
  // mesajı da "resource-exhausted" code'uyla geliyor; eski sıralama jenerik
  // "1 dakika bekle" gösterip kullanıcıyı boşuna tekrar denetiyordu.
  // Server mesajı "DAILY_LIMIT_EXCEEDED:<TR açıklama>" formatında — TR
  // kısmını doğrudan göster.
  if (code.includes("DAILY_LIMIT_EXCEEDED")) {
    const idx = code.indexOf("DAILY_LIMIT_EXCEEDED:");
    const detail = idx >= 0 ? code.slice(idx + "DAILY_LIMIT_EXCEEDED:".length).trim() : "";
    // EMİR #95.1 — yedek metin de KAYAN PENCEREYİ bilmeli: sunucu metni
    // gelmezse süreden türet, süre de yoksa takvim cümlesine düş (o modda doğru).
    return limitMesajiTR(detail, "Takip", (e as any)?.details?.retryAfterMs);
  }
  // P0-2 — followUserV3/unfollowUserV3 rate limit aşımı.
  // FirebaseError: code "functions/resource-exhausted", message "RATE_LIMITED".
  // FAZ 5 P2-3 — server `retryAfterMs` detayı varsa saniyeye çevirip göster.
  if (code.includes("RATE_LIMITED") || code.includes("resource-exhausted")) {
    const retryMs = Number(hata?.details?.retryAfterMs);
    if (Number.isFinite(retryMs) && retryMs > 0) {
      const sec = Math.max(1, Math.ceil(retryMs / 1000));
      return `Çok hızlı takip işlemi yapıyorsun. ${sec} saniye sonra tekrar dene.`;
    }
    return "Çok hızlı takip işlemi yapıyorsun. Lütfen 1 dakika bekle.";
  }
  if (code.includes("unauthenticated")) return "Giriş yapmalısın.";
  // FAZ 0 — block durumunda yön bilgisi sızdırılmaz (Instagram paritesi).
  if (code.includes("BLOCKED")) return "Bu kullanıcı takip edilemiyor.";
  if (code.includes("not-found")) return "Kullanıcı bulunamadı.";
  if (code.includes("permission-denied")) return "İzin reddedildi.";
  return "İşlem başarısız. Lütfen tekrar dene.";
}
